import React, { type JSX } from 'react';
import Loader from './Loader';
import styled, { DataAttributes } from 'styled-components';
import { base } from './BaseStyles';

export type TButtonVariant =
  | 'secondary'
  | 'link'
  | 'primary'
  | 'clear'
  | 'transparent'
  | 'tertiary'
  | 'transparent-bordered'
  | 'transparent-bordered-light'
  | undefined;

export interface IButton {
  children?: React.ReactNode;
  variant?: TButtonVariant;
  type?: 'submit' | 'reset' | 'button';
  block?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  hasMinWidth?: boolean;
  isLoading?: boolean;
  ariaLabel?: string;
  ariaExpanded?: boolean;
  ariaControls?: string;
  className?: string;
  size?: 'small' | 'medium' | 'large';
  dataTestId?: string;
}

const themeHeight = {
  small: '24px',
  medium: '32px',
  large: '43px',
};

const themePadding = {
  small: '0',
  medium: '0',
  large: '0 21px',
};

const StyledButton = styled.button.attrs<DataAttributes>((props: IButton) => ({
  'data-testid': props.dataTestId,
}))<IButton>`
  ${(props) => props.variant && base[props.variant]}

  ${(props) =>
    props.variant !== 'clear'
      ? `
    position: relative;

    padding: ${props.size && themePadding[props.size]};
    letter-spacing: normal;
    white-space: normal;
    height: ${props.size && themeHeight[props.size]};
    width: ${props.block ? '100%' : 'auto'};
    min-width: ${props.hasMinWidth ? '250px' : 'unset'};
    &:hover {
      background-color: ${() => {
        if (props.disabled && props.variant === 'primary') {
          return 'var(--clr-primary-1)';
        }

        if (props.disabled && props.variant === 'secondary') {
          return 'var(--black)';
        }

        return '';
      }};
    }
  `
      : ''}
`;

/**
 * @deprecated Use css utils .tw-lf-link or .tw-lf-button instead
 */
const Button = ({
  children,
  variant = 'primary' as const,
  type = 'button' as const,
  size = 'large' as const,
  block = true,
  onClick,
  disabled = false,
  hasMinWidth = true,
  isLoading = false,
  ariaLabel,
  ariaExpanded,
  ariaControls,
  className,
  dataTestId,
}: IButton): JSX.Element => {
  return (
    <StyledButton
      className={`${variant}${className ? ` ${className}` : ''}`}
      onClick={onClick}
      size={size}
      type={type}
      disabled={isLoading || disabled}
      block={block}
      variant={variant}
      hasMinWidth={hasMinWidth}
      aria-label={ariaLabel}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      isLoading={isLoading}
      dataTestId={dataTestId}
    >
      {isLoading ? <Loader /> : children}
    </StyledButton>
  );
};

export default Button;
